import { LICENSE_TOKEN_KEY, useLicense } from "@coach/license-client";
import { useEffect, useRef } from "react";
import { api } from "./api";
import { getOrCreateDeviceId } from "./device-id";
import { markServerConfirmedValid } from "./license-block-flag.js";
import { classifyRefreshResponse } from "./license-refresh-outcome.js";

/**
 * Background license heartbeat. Re-mints the cached JWT against the server
 * roughly once per day so the embedded `exp` is always far in the future.
 *
 * - Runs in every state except `unknown` (no token → `tick` bails anyway).
 *   That includes `blocked`: a blocked device with a stored (signature-valid)
 *   token asks once, 5 s after open. The server verifies with 1 year of
 *   tolerance and, if an admin has unlocked the org in the meantime, mints a
 *   fresh token → `setToken` → the gate opens. If it is still denied,
 *   `block()` is set again; `state.kind` stays `blocked`, so the effect does
 *   not re-run — no loop (next attempt after 24 h).
 * - Best-effort: a failed call leaves the existing token in place; the user
 *   can keep working offline until `graceUntil` runs out. Only an explicit
 *   denial blocks (see `classifyRefreshResponse`). A device with a persisted
 *   block (`license-block-flag.ts`) stays gated until this call — or
 *   `/billing/state` — confirms a valid license.
 * - Uses the public /license/refresh route (signature-verified), so it works
 *   even if the Better Auth session has lapsed.
 */
const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24h
const REFRESH_RETRY_MS = 60 * 60 * 1000; // 1h on failure

export function useLicenseAutoRefresh(): void {
  const { state, setToken, block } = useLicense();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `setToken` / `block` are fresh closures on every LicenseProvider render
  // (its context value is not memoized). Keep them out of the effect deps so
  // a repeated `block()` while already blocked can't re-arm the 5 s tick.
  const actionsRef = useRef({ setToken, block });
  actionsRef.current = { setToken, block };

  useEffect(() => {
    if (state.kind === "unknown") return;

    let cancelled = false;

    async function tick(): Promise<void> {
      let delay = REFRESH_INTERVAL_MS;
      try {
        const token = localStorage.getItem(LICENSE_TOKEN_KEY);
        if (!token) return;
        const deviceId = getOrCreateDeviceId();
        const resp = await api().license.refresh(
          token,
          deviceId ? { deviceId } : undefined,
        );
        const outcome = classifyRefreshResponse(resp);
        if (outcome.kind === "ok") {
          await actionsRef.current.setToken(outcome.token);
          markServerConfirmedValid();
        } else if (outcome.kind === "denied") {
          // The server reached us and explicitly rejected the license
          // (trial expired, subscription paused, license inactive…). The
          // offline JWT may still have a future `exp`, so block here rather
          // than letting the user keep working on a dead license.
          actionsRef.current.block(outcome.reason);
        } else {
          // 5xx / 429 / unexpected shape — never block on that; retry sooner.
          delay = REFRESH_RETRY_MS;
        }
      } catch {
        // Network/transport failure or non-JSON body — stay on the existing token and retry
        // sooner. Offline users keep working until `graceUntil` runs out.
        delay = REFRESH_RETRY_MS;
      } finally {
        if (!cancelled) {
          timerRef.current = setTimeout(tick, delay);
        }
      }
    }

    // Fire once shortly after mount so an expired license is caught on the
    // next app open instead of only after the 24h heartbeat interval.
    timerRef.current = setTimeout(tick, 5_000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [state.kind]);
}
